import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getProject } from '../lib/api';
import Loading from '../components/common/Loading';

const ProjectDetail = () => {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);

  const fetchProject = useCallback(async () => {
    try {
      const data = await getProject(slug);
      setProject(data);
    } catch (error) {
      console.error('Error fetching project:', error);
      setProject(null);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  if (loading) {
    return <Loading message="Proje detayları yükleniyor..." />;
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">Proje bulunamadı</h2>
          <p className="text-gray-600 mb-4">
            Aradığınız proje mevcut değil veya kaldırılmış olabilir.
          </p>
          <Link to="/projects" className="text-[#dbc582] hover:text-[#c5b274] transition-colors">
            Projelere dön
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <nav className="mb-8">
          <Link to="/projects" className="text-[#dbc582] hover:text-[#c5b274] transition-colors">
            ← Projelere Dön
          </Link>
        </nav>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-lg shadow-lg overflow-hidden"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-8">
            <div className="space-y-4">
              <div className="relative h-96">
                <img
                  src={project.images?.[selectedImage]}
                  alt={project.title}
                  className="w-full h-full object-cover rounded-lg"
                />
              </div>
              <div className="flex gap-2 overflow-x-auto py-2">
                {(project.images || []).map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`relative w-20 h-20 flex-shrink-0 ${
                      selectedImage === index ? 'ring-2 ring-[#dbc582]' : ''
                    }`}
                  >
                    <img
                      src={image}
                      alt={`${project.title} ${index + 1}`}
                      className="w-full h-full object-cover rounded"
                    />
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">{project.title}</h1>
                <p className="text-gray-600">{project.description}</p>
              </div>

              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">Proje Detayları</h2>
                <dl className="grid grid-cols-1 gap-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Konum</dt>
                    <dd className="text-base text-gray-900">{project.location}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Tamamlanma Tarihi</dt>
                    <dd className="text-base text-gray-900">
                      {new Date(project.completion_date).toLocaleDateString('tr-TR')}
                    </dd>
                  </div>
                </dl>
              </div>

              {project.features?.length > 0 && (
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">Özellikler</h2>
                  <ul className="list-disc list-inside space-y-2">
                    {project.features.map((feature, index) => (
                      <li key={index} className="text-gray-600">
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {project.specifications?.length > 0 && (
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">Teknik Özellikler</h2>
                  <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {project.specifications.map((spec, index) => (
                      <div key={index}>
                        <dt className="text-sm font-medium text-gray-500">{spec.label}</dt>
                        <dd className="text-base text-gray-900">{spec.value}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ProjectDetail;
